import {type BucketedHistoryResponse} from '../../../../generated/historyapi/history_pb';
import {
  recordRumDurationVital,
  recordRumError,
} from '../../../../util/datadog/datadogRum';
import * as Filter from '../../../../util/filters';
import {historyClient} from '../../../../util/historyConnect';
import {
  filterRunIdsToProject,
  pinnedRunIdsFilter,
} from '../../../../util/hooks/usePinnedRunsFilters';
import {getRunState, parseConfig, parseSummary} from '../../../../util/runs';
import {runIdsToNames, runIdToProject} from '../../../../util/selectionmanager';
import {type HistoryColumns} from '../../componentOutliers/types';
import {type BucketedLine} from '../../types';
import {type QueryVars} from '../queries/queryVars';
import {getLineGroup, trackedBucketedDataEvent} from '../queries/utils';

// A project one request is scoped to: the panel's own, or one holding pins.
type ProjectRef = {entityName: string; projectName: string};

type ColumnarRequest = {
  project: ProjectRef;
  runFilter: string;
  limit: number;
  pinnedRuns: string[];
  currentRuns: string[];
};

// Fetches bucketed history over Connect, one packed sketch per run per spec.
export function fetchHistoryColumnar(
  vars: QueryVars,
  request: ColumnarRequest,
  lastUpdated: string | undefined
): Promise<BucketedHistoryResponse> {
  return historyClient.getBucketedHistory({
    entity: request.project.entityName,
    project: request.project.projectName,
    runFilter: request.runFilter,
    specs: vars.bucketedHistorySpecs,
    limit: request.limit,
    order: vars.order,
    currentRuns: request.currentRuns,
    lastUpdated,
    configKeys: vars.configKeys ?? [],
    summaryKeys: vars.summaryKeys ?? [],
    pinnedRuns: request.pinnedRuns,
  });
}

const EPOCH_ISO = new Date(0).toISOString();

function toIso(raw: string | undefined): string {
  return raw ? new Date(raw).toISOString() : EPOCH_ISO;
}

function projectKeyOf(project: ProjectRef): string {
  // Entity and project names are case-insensitive at the data layer.
  return `${project.entityName.toLowerCase()}/${project.projectName.toLowerCase()}`;
}

function sameProject(a: ProjectRef, b: ProjectRef): boolean {
  return projectKeyOf(a) === projectKeyOf(b);
}

// Groups foreign pins by project; the endpoint resolves names one project at a time.
function foreignPinsByProject(
  pinnedRunIds: string[],
  panelProject: ProjectRef
): Array<{project: ProjectRef; ids: string[]}> {
  const byProject = new Map<string, {project: ProjectRef; ids: string[]}>();
  for (const id of pinnedRunIds) {
    const project = runIdToProject(id);
    if (project == null || sameProject(project, panelProject)) {
      continue;
    }
    const key = projectKeyOf(project);
    const group = byProject.get(key);
    if (group == null) {
      byProject.set(key, {project, ids: [id]});
    } else {
      group.ids.push(id);
    }
  }
  return [...byProject.values()];
}

// The wire speaks names, so a request declares only the held runs from its own project.
function heldNamesInProject(runIds: string[], project: ProjectRef): string[] {
  return runIdsToNames(filterRunIdsToProject(runIds, project));
}

// Indexes held lines per project so `order`, which carries names, resolves without collisions.
function indexHeldByProject(
  currentRunsById: Record<string, BucketedLine>
): Map<string, Map<string, BucketedLine>> {
  const byProject = new Map<string, Map<string, BucketedLine>>();
  for (const [key, line] of Object.entries(currentRunsById)) {
    const project = runIdToProject(key);
    const projectKey = project == null ? '' : projectKeyOf(project);
    let byName = byProject.get(projectKey);
    if (byName == null) {
      byName = new Map();
      byProject.set(projectKey, byName);
    }
    byName.set(line.name, line);
  }
  return byProject;
}

type DecodeTotals = {
  specCount: number;
  wireBytes: number;
  skippedRunCount: number;
};

// Turns one project's response into ID-keyed lines plus its slice of the chart order.
function decodeResponse(
  response: BucketedHistoryResponse,
  project: ProjectRef,
  decodeSketch: (bytes: Uint8Array) => HistoryColumns,
  heldByName: Map<string, BucketedLine> | undefined,
  totals: DecodeTotals
): {
  lines: Record<string, BucketedLine>;
  order: string[];
  incomplete: string[];
} {
  const decodedByName: Record<string, BucketedLine> = {};
  const incompleteNames = new Set<string>();
  const keyByName = new Map<string, string>();

  for (const run of response.runs) {
    // Built from canonical names server-side, so it can't be rebuilt from the request.
    const runId = run.runId || run.runName;
    keyByName.set(run.runName, runId);
    if (run.incomplete) {
      incompleteNames.add(run.runName);
      continue;
    }
    // Single pass: tally wire bytes and decode non-empty payloads together.
    let historyColumnar: BucketedLine['historyColumnar'];
    try {
      const cols: BucketedLine['historyColumnar'] = [];
      for (const bytes of run.specsPacked) {
        totals.specCount++;
        totals.wireBytes += bytes.length;
        if (bytes.length > 0) {
          cols.push(decodeSketch(bytes));
        }
      }
      historyColumnar = cols;
    } catch (e) {
      // A malformed packed payload skips just this run, not the whole batch.
      totals.skippedRunCount++;
      console.error(
        `Error decoding columnar bucketed history for run ${run.runName}`,
        e
      );
      recordRumError(e, {
        source: 'bucketed_columnar_decode',
        runName: run.runName,
        entity: project.entityName,
        project: project.projectName,
      });
      continue;
    }
    // Reuse the GraphQL path's normalizers so both paths agree on KeyVal shape.
    const parsedConfig =
      run.config != null ? parseConfig(run.config, run.runName) : null;
    const parsedSummary =
      run.summary != null ? parseSummary(run.summary, run.runName) : null;
    decodedByName[run.runName] = {
      id: runId,
      name: run.runName,
      displayName: run.displayName || run.runName,
      defaultColorIndex: run.defaultColorIndex,
      createdAt: toIso(run.createdAt),
      updatedAt: toIso(run.updatedAt),
      heartbeatAt: toIso(run.heartbeatAt),
      group: run.groupName ?? null,
      host: run.host ?? null,
      jobType: null,
      state: getRunState(run.state),
      stopped: run.stopped,
      user: {name: '', photoUrl: ''},
      _wandb: parsedConfig?._wandb ?? {},
      config: parsedConfig?.config ?? {},
      summary: parsedSummary ?? {},
      aggregations: {min: {}, max: {}},
      tags: [],
      history: [],
      historyColumnar,
    };
  }

  const names =
    response.order && response.order.length > 0
      ? response.order
      : Object.keys(decodedByName);
  const lines: Record<string, BucketedLine> = {};
  const order: string[] = [];
  const incomplete: string[] = [];
  for (const name of names) {
    const line = decodedByName[name] ?? heldByName?.get(name);
    const key = keyByName.get(name) ?? line?.id;
    if (key == null) {
      continue;
    }
    // An incomplete run keeps its slot in the order but carries no line.
    order.push(key);
    if (incompleteNames.has(name)) {
      incomplete.push(key);
      continue;
    }
    if (line != null) {
      lines[key] = line;
    }
  }
  return {lines, order, incomplete};
}

export async function columnarBucketedQuery(
  vars: QueryVars,
  runIds: string[],
  lastUpdated: string,
  currentRunsById: Record<string, BucketedLine>
): Promise<{
  incomplete: string[];
  retryAfterSeconds: number | undefined;
  isAggregated: boolean;
  order: string[];
  runsById: Record<string, BucketedLine>;
}> {
  const isInitialQuery = runIds.length === 0;
  const panelProject: ProjectRef = {
    entityName: vars.entityName,
    projectName: vars.projectName,
  };
  const pinnedRunIds = vars.pinnedRunIds;
  const mainRequest: ColumnarRequest = {
    project: panelProject,
    runFilter: vars.filters,
    limit: vars.limit,
    // queryVars already excluded these and shrank the limit, so naming them cannot double-count.
    pinnedRuns: runIdsToNames(
      filterRunIdsToProject(pinnedRunIds, panelProject)
    ),
    currentRuns: heldNamesInProject(runIds, panelProject),
  };
  const foreignRequests = foreignPinsByProject(pinnedRunIds, panelProject).map(
    ({project, ids}): ColumnarRequest => ({
      project,
      // The panel's filter describes its own project, so a foreign pin is selected by name.
      runFilter: JSON.stringify(Filter.toMongo(pinnedRunIdsFilter(ids))),
      limit: ids.length,
      pinnedRuns: [],
      currentRuns: heldNamesInProject(runIds, project),
    })
  );

  // Epoch-anchored so the whole fetch + decode can be reported as a duration vital.
  const startTime = performance.timeOrigin + performance.now();
  const fetchStart = performance.now();
  const since = isInitialQuery ? undefined : lastUpdated;
  const decoderModule = import('./decodeSketchPacked');
  const mainFetch = fetchHistoryColumnar(vars, mainRequest, since);
  // A foreign project the viewer can't read drops that pin instead of failing the panel.
  const foreignFetch = Promise.allSettled(
    foreignRequests.map(request => fetchHistoryColumnar(vars, request, since))
  );
  let response: BucketedHistoryResponse;
  try {
    response = await mainFetch;
  } catch (e) {
    // Whole-query fetch failure, distinct from a per-run decode failure.
    recordRumError(e, {
      source: 'bucketed_columnar_fetch',
      entity: vars.entityName,
      project: vars.projectName,
    });
    throw e;
  }
  const foreignSettled = await foreignFetch;
  const fetchMs = performance.now() - fetchStart;

  // The first decoder chunk load overlaps the request instead of extending the
  // response-handling critical path.
  const decodeStart = performance.now();
  const {decodeSketchPacked} = await decoderModule;

  const heldByProject = indexHeldByProject(currentRunsById);
  const totals: DecodeTotals = {
    specCount: 0,
    wireBytes: 0,
    skippedRunCount: 0,
  };
  const decodeOptions =
    (vars.groupKeys?.length ?? 0) === 0
      ? ({
          mode: 'ungrouped',
          minMaxLast: vars.primaryLineType === 'min-max-last',
        } as const)
      : undefined;
  const decodeFor = (res: BucketedHistoryResponse, project: ProjectRef) =>
    decodeResponse(
      res,
      project,
      bytes => decodeSketchPacked(bytes, decodeOptions),
      heldByProject.get(projectKeyOf(project)) ?? heldByProject.get(''),
      totals
    );

  const runsById: Record<string, BucketedLine> = {};
  const order: string[] = [];
  const incomplete: string[] = [];
  let runCount = response.runs.length;

  // Cross-project pins lead the chart; same-project pins already lead the main response.
  foreignSettled.forEach((settled, i) => {
    const request = foreignRequests[i];
    if (settled.status === 'rejected') {
      recordRumError(settled.reason, {
        source: 'bucketed_columnar_pinned_fetch',
        entity: request.project.entityName,
        project: request.project.projectName,
      });
      return;
    }
    runCount += settled.value.runs.length;
    const decoded = decodeFor(settled.value, request.project);
    Object.assign(runsById, decoded.lines);
    order.push(...decoded.order);
    incomplete.push(...decoded.incomplete);
  });

  const main = decodeFor(response, panelProject);
  Object.assign(runsById, main.lines);
  order.push(...main.order);
  incomplete.push(...main.incomplete);

  const decodeMs = performance.now() - decodeStart;
  const numMetrics = vars.bucketedHistorySpecs.length;
  const loadedRunCount = Object.keys(runsById).length;
  trackedBucketedDataEvent('Columnar_query_complete', {
    runCount,
    decodedRunCount: loadedRunCount,
    skippedRunCount: totals.skippedRunCount,
    specCount: totals.specCount,
    wireBytes: totals.wireBytes,
  });

  // Emit the same vital as the record path so columnar panels land in existing
  // LinePlotQueryProfile dashboards; extra columnar-only fields ride in context.
  recordRumDurationVital('LinePlotQueryProfile', {
    description: 'Measures the performance of the line plot query',
    startTime,
    duration: performance.timeOrigin + performance.now() - startTime,
    context: {
      behavior: 'BucketedColumnarQuery',
      bins: vars.bins,
      isInitialQuery,
      isMultiMetrics: numMetrics > 1,
      isSystemMetrics: vars.isSystemMetrics,
      isThrottledMultiMetrics: false,
      groupKeys: vars.groupKeys,
      hasIncomplete: incomplete.length > 0,
      incompleteCount: incomplete.length,
      internalId: vars.internalId,
      maxGroups: vars.maxGroups,
      metricNames: vars.metricNames,
      numMetrics,
      loadedRunCount,
      numSpecs: numMetrics,
      order: vars.order,
      panelType: vars.panelType,
      project: vars.projectName,
      totalLines: loadedRunCount * numMetrics,
      lineGroup: getLineGroup(loadedRunCount * numMetrics),
      pinnedRunCount: pinnedRunIds.length,
      crossProjectPinFetches: foreignRequests.length,
      skippedRunCount: totals.skippedRunCount,
      specCount: totals.specCount,
      wireBytes: totals.wireBytes,
      fetchMs,
      decodeMs,
    },
  });

  return {
    incomplete,
    retryAfterSeconds: undefined,
    isAggregated: false,
    order,
    runsById,
  };
}
